import React from "react";
import { Tag, Button, Tooltip } from "antd";
import {
  UserOutlined,
  PhoneOutlined,
  TeamOutlined,
  CheckOutlined,
  CloseOutlined,
  CoffeeOutlined,
  EditOutlined,
  CalendarOutlined,
} from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import type { Reservation, ReservationStatus } from "../../../../utils/api/reservationsAPI";
import "./ReservationCard.scss";

export const STATUS_CONFIG: Record<
  ReservationStatus,
  { label: string; color: string; bg: string; dot: string }
> = {
  pending:    { label: "Pending",    color: "#b45309", bg: "#fef3c7", dot: "#f59e0b" },
  confirmed:  { label: "Confirmed",  color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  seated:     { label: "Seated",     color: "#1e40af", bg: "#dbeafe", dot: "#3b82f6" },
  completed:  { label: "Completed",  color: "#374151", bg: "#f3f4f6", dot: "#9ca3af" },
  paid:       { label: "Paid",       color: "#065f46", bg: "#d1fae5", dot: "#10b981" },
  waitlisted: { label: "Waitlisted", color: "#6d28d9", bg: "#ede9fe", dot: "#7c3aed" },
  cancelled:  { label: "Cancelled",  color: "#991b1b", bg: "#fee2e2", dot: "#ef4444" },
  "no-show":  { label: "No Show",    color: "#4b5563", bg: "#f3f4f6", dot: "#6b7280" },
};

type Props = {
  reservation: Reservation;
  onClick: () => void;
  onStatusChange?: (id: string, status: ReservationStatus) => void;
  onEdit?: (reservation: Reservation) => void;
  compact?: boolean;
};

const SOURCE_CONFIG: Record<string, { label: string; bg: string; color: string; icon: string }> = {
  internal: { label: "Interno",  bg: "#f3f4f6", color: "#6b7280", icon: "🖥️" },
  website:  { label: "Online",   bg: "#dbeafe", color: "#1d4ed8", icon: "🌐" },
  widget:   { label: "Widget",   bg: "#d1fae5", color: "#065f46", icon: "🔗" },
  phone:    { label: "Telefon",  bg: "#fef3c7", color: "#92400e", icon: "📞" },
  voice:    { label: "Voice",    bg: "#ede9fe", color: "#5b21b6", icon: "🎙️" },
};

const ReservationCard: React.FC<Props> = ({
  reservation,
  onClick,
  onStatusChange,
  onEdit,
  compact = false,
}) => {
  const { t } = useTranslation();
  const cfg = STATUS_CONFIG[reservation.status];
  const src = SOURCE_CONFIG[reservation.source] ?? SOURCE_CONFIG.internal;

  const handleAction = (e: React.MouseEvent, action: () => void) => {
    e.stopPropagation();
    action();
  };

  return (
    <div
      className={`res-card res-card--${reservation.status}${compact ? " res-card--compact" : ""}`}
      onClick={onClick}
      style={{ borderLeftColor: cfg.dot }}
    >
      <div className="res-card__header">
        <div className="res-card__time">{reservation.time}</div>
        <div className="res-card__header-right">
          <span
            className="res-card__status"
            style={{ color: cfg.color, background: cfg.bg }}
          >
            <span className="res-card__dot" style={{ background: cfg.dot }} />
            {t(cfg.label)}
          </span>
          <span
            className="res-card__source"
            style={{ background: src.bg, color: src.color }}
            title={src.label}
          >
            {src.icon} {src.label}
          </span>
        </div>
      </div>

      {reservation.eventId && (
        <div className="res-card__event-badge">
          <CalendarOutlined />
          <span>{typeof reservation.eventId === "object" ? reservation.eventId.title : t("Event")}</span>
        </div>
      )}

      <div className="res-card__guest">
        <UserOutlined />
        <span className="res-card__name">{reservation.guestName}</span>
      </div>

      <div className="res-card__meta">
        <span className="res-card__meta-item">
          <PhoneOutlined />
          {reservation.guestPhone}
        </span>
        <span className="res-card__meta-item">
          <TeamOutlined />
          {reservation.numberOfGuests} {t("guests")}
        </span>
        {reservation.waiterName && (
          <span className="res-card__meta-item">
            <CoffeeOutlined />
            {reservation.waiterName}
          </span>
        )}
      </div>

      {(reservation.tableNames?.length || reservation.tableName) && (
        <div className="res-card__tables">
          {(reservation.tableNames?.length
            ? reservation.tableNames
            : [reservation.tableName!]
          ).map((name) => (
            <Tag key={name} color="blue" style={{ fontSize: 11, margin: "2px 2px 0 0" }}>
              {name}
            </Tag>
          ))}
        </div>
      )}

      {reservation.note && !compact && (
        <div className="res-card__note">{reservation.note}</div>
      )}

      {onStatusChange && !compact && (
        <div className="res-card__actions" onClick={(e) => e.stopPropagation()}>
          {reservation.status === "pending" && (
            <Tooltip title={t("Confirm")}>
              <Button
                size="small"
                type="primary"
                icon={<CheckOutlined />}
                className="res-card__btn res-card__btn--confirm"
                onClick={(e) =>
                  handleAction(e, () => onStatusChange(reservation._id, "confirmed"))
                }
              >
                {t("Confirm")}
              </Button>
            </Tooltip>
          )}
          {reservation.status === "confirmed" && (
            <Tooltip title={t("Mark as Seated")}>
              <Button
                size="small"
                icon={<CoffeeOutlined />}
                className="res-card__btn res-card__btn--seat"
                onClick={(e) =>
                  handleAction(e, () => onStatusChange(reservation._id, "seated"))
                }
              >
                {t("Seated")}
              </Button>
            </Tooltip>
          )}
          {(reservation.status === "pending" || reservation.status === "confirmed") && (
            <Tooltip title={t("Cancel")}>
              <Button
                size="small"
                danger
                icon={<CloseOutlined />}
                className="res-card__btn"
                onClick={(e) =>
                  handleAction(e, () => onStatusChange(reservation._id, "cancelled"))
                }
              />
            </Tooltip>
          )}
          {onEdit && (
            <Tooltip title={t("Edit")}>
              <Button
                size="small"
                icon={<EditOutlined />}
                className="res-card__btn"
                onClick={(e) => handleAction(e, () => onEdit(reservation))}
              />
            </Tooltip>
          )}
        </div>
      )}
    </div>
  );
};

export default ReservationCard;
